import { Stack } from '@mui/material';
import { InviteStatus } from '@schooly/constants';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { InviteStatusTagSelect, InviteStatusTagSelectProps } from './InviteStatusTagSelect';

type InviteStatusExpandedSelectProps = PropsWithChildren<{
  selectedStatuses: InviteStatus[];
  onApply: (v: InviteStatus[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;

const OPTIONS = [InviteStatus.Invited, InviteStatus.NotInvited, InviteStatus.InviteAccepted];

export const InviteStatusExpandedSelect: FC<InviteStatusExpandedSelectProps> = ({
  selectedStatuses: initialSelectedStatuses,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedStatuses, setSelectedStatuses] = useState<InviteStatus[]>(initialSelectedStatuses);

  const toggleStatuses = useCallback((v: InviteStatus[]) => {
    setSelectedStatuses((statuses) => toggleMultipleValueArrayProperty(statuses, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedStatuses);
    onClose();
  }, [selectedStatuses, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderInviteStatusTags({
            statuses: OPTIONS,
            tagProps: (status) => ({
              variant: selectedStatuses.includes(status) ? 'filled' : undefined,
            }),
            onClick: (status) => toggleStatuses([status]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleStatuses, selectedStatuses]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedStatuses.length > 0}
      onClose={onClose}
      onClear={onClear}
      renderContent={renderContent}
    >
      {renderInviteStatusTags({
        tagProps: { size: 'small' },
        statuses: selectedStatuses,
        onDelete: (status) => toggleStatuses([status]),
      })}
    </ExpandedSelect>
  );
};

type RenderInviteStatusTagsParams = {
  statuses: InviteStatus[];
  onDelete?: (v: InviteStatus) => void;
  onClick?: (v: InviteStatus) => void;
  tagProps?:
    | Omit<InviteStatusTagSelectProps, 'status'>
    | ((v: InviteStatus) => Omit<InviteStatusTagSelectProps, 'status'>);
};

export const renderInviteStatusTags = ({
  statuses,
  onDelete,
  onClick,
  tagProps,
}: RenderInviteStatusTagsParams) =>
  statuses.map((v) => (
    <InviteStatusTagSelect
      key={v}
      status={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
